import { FlowStep } from '@/types/flow';
import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import PhotoCarousel from './PhotoCarousel';
import BeforeAfterComparison from './BeforeAfterComparison';
import SkinProfileSummary from './SkinProfileSummary';
import { decorateShopUrl } from '@/lib/tracking';

interface ScreenRendererProps {
  steps: FlowStep[];
  onAdvance: () => void;
  accentColor?: string;
  onAnswer?: (stepId: string, questionTitle: string, selectedLabels: string[]) => void;
}

/** Replace dynamic placeholders like {{TARGET_DATE}} */
function resolvePlaceholders(text: string): string {
  if (text.includes('{{TARGET_DATE}}')) {
    const d = new Date();
    d.setMonth(d.getMonth() + 3);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    text = text.replace(/\{\{TARGET_DATE\}\}/g, `${dd}/${mm}/${yyyy}`);
  }
  return text;
}

/** Render a text line with **bold** and [link](url) support */
function renderTextLine(line: string): React.ReactNode {
  if (!line) return <br />;
  const parts = line.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, j) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={j} className="text-gray-900 font-bold">{part.slice(2, -2)}</strong>;
    }
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      return <a key={j} href={decorateShopUrl(linkMatch[2])} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{linkMatch[1]}</a>;
    }
    return part;
  });
}

const ScreenRenderer = ({ steps, onAdvance, accentColor, onAnswer }: ScreenRendererProps) => {
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string[]>>({});
  const [loaderStep, setLoaderStep] = useState(0);
  const [loaderDone, setLoaderDone] = useState(false);

  const loaderBlock = steps.find(s => s.type === 'loader' || s.type === 'list_loader');
  const hasHeadline = steps.some(s => s.type === 'headline');
  const firstQuestionId = steps.find(s =>
    ['multiple_choice', 'select', 'picture_choice'].includes(s.type)
  )?.id;
  const QuestionHeading = !hasHeadline ? 'h1' : 'h2';

  // Loader animation
  useEffect(() => {
    if (!loaderBlock?.loaderSteps) return;
    setLoaderStep(0);
    setLoaderDone(false);
    const totalSteps = loaderBlock.loaderSteps.length;
    const duration = loaderBlock.loaderDuration || 4000;
    const interval = duration / totalSteps;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      if (step >= totalSteps) {
        clearInterval(timer);
        setLoaderStep(totalSteps - 1);
        setTimeout(() => {
          setLoaderDone(true);
          onAdvance();
        }, 1000);
      } else {
        setLoaderStep(step);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [loaderBlock?.id]);

  const handleOptionClick = useCallback((stepId: string, optionId: string, multiSelect?: boolean, autoAdvance?: boolean) => {
    const step = steps.find(s => s.id === stepId);
    setSelectedOptions(prev => {
      const current = prev[stepId] || [];
      let next: string[];
      if (multiSelect) {
        next = current.includes(optionId) ? current.filter(id => id !== optionId) : [...current, optionId];
      } else {
        next = [optionId];
      }

      if (onAnswer && step?.options) {
        const labels = next.map(id => step.options!.find(o => o.id === id)?.label || id);
        onAnswer(stepId, step.title, labels);
      }

      return { ...prev, [stepId]: next };
    });

    if (!multiSelect) {
      setTimeout(onAdvance, 400);
    }
  }, [onAdvance, onAnswer, steps]);

  return (
    <div className="space-y-4 sm:space-y-5 max-w-lg lg:max-w-xl mx-auto">
      {steps.map((step) => {
        if (['continue_button', 'submit_button', 'back_button', 'link_button', 'universal_button'].includes(step.type)) {
          return null;
        }

        const align = step.settings?.align || 'center';
        const textAlign = align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';

        switch (step.type) {
          case 'headline':
            return (
              <div key={step.id} className={textAlign}>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-gray-900 leading-tight">
                  {(() => {
                    const resolved = resolvePlaceholders(step.title);
                    return resolved.split('€60').length > 1 ? (
                      <>
                        {resolved.split('€60')[0]}
                        <span className="text-green-600 underline decoration-2">€60</span>
                        {resolved.split('€60')[1]}
                      </>
                    ) : resolved;
                  })()}
                </h1>
              </div>
            );

          case 'text': {
            const fontSize = step.design?.fontSize ? `${step.design.fontSize}px` : '17px';
            return (
              <div key={step.id} className={textAlign}>
              {resolvePlaceholders(step.title).split('\n').map((line, i) => (
                  <p key={i} style={{ fontSize }} className={`text-gray-500 leading-relaxed ${i > 0 ? 'mt-2' : ''}`}>
                    {renderTextLine(line)}
                  </p>
                ))}
              </div>
            );
          }

          case 'multiple_choice':
          case 'select':
            return (
              <div key={step.id} className="space-y-3">
                {step.id === firstQuestionId ? (
                  <QuestionHeading className={`text-lg sm:text-xl font-bold text-gray-900 ${textAlign}`}>{step.title}</QuestionHeading>
                ) : (
                  <h2 className={`text-lg sm:text-xl font-bold text-gray-900 ${textAlign}`}>{step.title}</h2>
                )}
                {step.description && (
                  <p className={`text-[17px] text-gray-400 ${textAlign}`}>{step.description}</p>
                )}
                <div className="space-y-2 lg:space-y-3 mt-3 sm:mt-4">
                  {step.options?.map((opt, i) => {
                    const isSelected = (selectedOptions[step.id] || []).includes(opt.id);
                    const emojiMatch = opt.label.match(/^([\p{Emoji_Presentation}\p{Extended_Pictographic}][\uFE0F\u200D\p{Emoji_Presentation}\p{Extended_Pictographic}]*)\s+/u);
                    const emoji = emojiMatch ? emojiMatch[1].trim() : null;
                    const label = emoji ? opt.label.slice(emojiMatch[0].length) : opt.label;
                    const dashIdx = label.indexOf('–');
                    const mainLabel = dashIdx > 0 ? label.slice(0, dashIdx).trim() : label;
                    const subLabel = dashIdx > 0 ? label.slice(dashIdx + 1).trim() : null;

                    return (
                      <motion.button
                        key={opt.id}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleOptionClick(step.id, opt.id, step.multiSelect, step.autoAdvance)}
                        className={`w-full flex items-center gap-3 rounded-xl border p-3 sm:p-4 lg:p-5 text-left transition-all duration-200 ${
                          isSelected
                            ? 'border-[#0F1C2E] bg-[#0F1C2E] text-white shadow-md'
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm active:scale-[0.98]'
                        }`}
                      >
                        <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                          isSelected ? 'border-white bg-white' : 'border-gray-300'
                        }`}>
                          {isSelected && <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[#0F1C2E]" />}
                        </div>
                        {opt.imageUrl ? (
                          <img src={opt.imageUrl} alt={label} className="w-8 h-8 rounded-full object-cover shrink-0" />
                        ) : emoji ? (
                          <span className="text-lg sm:text-xl shrink-0">{emoji}</span>
                        ) : null}
                        <div className="flex-1 min-w-0">
                          <span className={`text-[17px] font-medium ${isSelected ? 'text-white' : 'text-gray-900'}`}>{mainLabel}</span>
                          {subLabel && (
                            <span className={`block text-sm mt-0.5 ${isSelected ? 'text-white/70' : 'text-gray-400'}`}>{subLabel}</span>
                          )}
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            );

          case 'picture_choice':
            return (
              <div key={step.id} className="space-y-3">
                {step.id === firstQuestionId ? (
                  <QuestionHeading className={`text-lg sm:text-xl font-bold text-gray-900 ${textAlign}`}>{step.title}</QuestionHeading>
                ) : (
                  <h2 className={`text-lg sm:text-xl font-bold text-gray-900 ${textAlign}`}>{step.title}</h2>
                )}
                {step.description && (
                  <p className={`text-[17px] text-gray-400 ${textAlign}`}>{step.description}</p>
                )}
                <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-3 sm:mt-4">
                  {step.options?.map((opt, i) => {
                    const isSelected = (selectedOptions[step.id] || []).includes(opt.id);
                    return (
                      <motion.button
                        key={opt.id}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => handleOptionClick(step.id, opt.id, step.multiSelect, step.autoAdvance)}
                        className={`relative rounded-xl border-2 overflow-hidden transition-all duration-200 ${
                          isSelected
                            ? 'border-[#0F1C2E] shadow-md ring-2 ring-blue-100'
                            : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                        }`}
                      >
                        {opt.imageUrl ? (
                          <img src={opt.imageUrl} alt={opt.label} className="w-full aspect-square object-cover" loading="lazy" />
                        ) : (
                          <div className="w-full aspect-square bg-gray-100 flex items-center justify-center">
                            <span className="text-3xl">❓</span>
                          </div>
                        )}
                        <div className="px-2 sm:px-3 py-1.5 sm:py-2 text-center">
                          <span className="text-sm font-medium text-gray-900">{opt.label}</span>
                        </div>
                        <div className={`absolute top-2 right-2 w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center text-[10px] font-bold transition-colors ${
                          isSelected ? 'border-[#0F1C2E] bg-[#0F1C2E] text-white' : 'border-white/80 bg-white/60 text-gray-400'
                        }`}>
                          {isSelected ? '✓' : i + 1}
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            );

          case 'loader':
          case 'list_loader':
            if (!step.loaderSteps) {
              return (
                <div key={step.id} className="flex flex-col items-center py-12">
                  <Loader2 className="h-10 w-10 animate-spin text-[#0F1C2E]" />
                  <p className="mt-4 text-[17px] text-gray-500">{step.title}</p>
                </div>
              );
            }
            return (
              <div key={step.id} className="space-y-4 py-6">
                <div className="space-y-3">
                  {step.loaderSteps.map((ls, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={i <= loaderStep ? { opacity: 1, x: 0 } : { opacity: 0.3, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.1 }}
                      className="flex items-center gap-3"
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs transition-colors duration-500 ${
                        i < loaderStep ? 'bg-green-500 text-white' : i === loaderStep ? 'bg-[#0F1C2E] text-white animate-pulse' : 'bg-gray-200 text-gray-400'
                      }`}>
                        {i < loaderStep ? '✓' : i === loaderStep ? '•' : ''}
                      </div>
                      <span className={`text-[17px] font-medium transition-colors duration-300 ${
                        i <= loaderStep ? 'text-gray-900' : 'text-gray-300'
                      }`}>
                        {ls}
                      </span>
                    </motion.div>
                  ))}
                </div>
                {!loaderDone && (
                  <div className="flex justify-center pt-4">
                    <p className="text-sm text-gray-400 animate-pulse">Even geduld...</p>
                  </div>
                )}
              </div>
            );

          case 'photo_carousel': {
            const images = step.images || [];
            if (images.length === 0) return null;
            return <PhotoCarousel key={step.id} images={images} />;
          }

          case 'before_after':
            return (
              <BeforeAfterComparison
                key={step.id}
                beforeImage="https://cdn.shopify.com/s/files/1/1078/0940/7317/files/quiz-before-woman.png?v=1789291307&width=800"
                afterImage="https://cdn.shopify.com/s/files/1/1078/0940/7317/files/quiz-after-woman.png?v=1789291307&width=800"
                beforeMetrics={[
                  { label: 'Huidelasticiteit', value: 'Laag', level: 1, color: 'red' },
                  { label: 'Rimpels & Fijne lijnen', value: 'Gemiddeld tot diep', level: 1, color: 'red' },
                  { label: 'Collageen niveau', value: 'Ondergemiddeld', level: 1, color: 'red' },
                ]}
                afterMetrics={[
                  { label: 'Huidelasticiteit', value: 'Hoog', level: 4, color: 'green' },
                  { label: 'Rimpels & Fijne lijnen', value: 'Zichtbaar verminderd', level: 4, color: 'green' },
                  { label: 'Collageen niveau', value: 'Hoog', level: 4, color: 'green' },
                ]}
              />
            );

          case 'skin_profile_summary':
            return <SkinProfileSummary key={step.id} />;

          case 'image':
            return (
              <div key={step.id} className="rounded-xl overflow-hidden">
                {step.imageUrl ? (
                  <img src={step.imageUrl} alt={step.title} className="w-full rounded-xl" loading="lazy" />
                ) : (
                  <div className="w-full aspect-video bg-gray-100 flex items-center justify-center rounded-xl">
                    <span className="text-gray-300">Image</span>
                  </div>
                )}
              </div>
            );

          default:
            return null;
        }
      })}
    </div>
  );
};

export default ScreenRenderer;
